/**
 * README "Add website dialog — 400px". One field, normalised silently as
 * the user types; a paste that changes something flips the hint into a
 * confirmation line. Three validation states (not a domain / already
 * listed / quota reached), all resolved client-side against the already-
 * fetched `existingDomains` list and the org's quota — no round trip
 * needed to tell the user which one applies.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Modal from "../../theme/untitled/Modal/Modal";
import Input from "../../theme/untitled/Input/Input";
import Button from "../../theme/untitled/Button/Button";
import Icon from "../../theme/untitled/icons/Icon";
import { formatDateShort } from "../../../../helpers/formatters";
import {
  isValidDomain,
  isWildcardDomain,
  normalizeDomain,
} from "./domainNormalize";
import styles from "./AddWebsiteDialog.module.scss";

const AddWebsiteDialog = ({
  open,
  onClose,
  onSubmit,
  sending,
  submitError,
  existingDomains,
  domainsUsed,
  domainLimit,
}) => {
  const history = useHistory();
  const [rawValue, setRawValue] = useState("");
  const [justPasted, setJustPasted] = useState(false);

  useEffect(() => {
    if (open) {
      setRawValue("");
      setJustPasted(false);
    }
  }, [open]);

  const normalized = normalizeDomain(rawValue);
  const trimmedRaw = rawValue.trim();
  const isEmpty = trimmedRaw.length === 0;
  const isWildcard = !isEmpty && isWildcardDomain(normalized);
  const isValidFormat = !isEmpty && !isWildcard && isValidDomain(normalized);
  const duplicate = !isEmpty
    ? existingDomains.find((d) => d.domain.toLowerCase() === normalized)
    : null;

  const quotaReached =
    domainLimit != null && domainLimit >= 0 && domainsUsed >= domainLimit;

  const handleChange = useCallback((e) => {
    const value = e.target.value;
    setRawValue(value);

    const isPasteInput = e.nativeEvent?.inputType === "insertFromPaste";
    if (isPasteInput) {
      const norm = normalizeDomain(value);
      setJustPasted(norm.length > 0 && norm !== value.trim().toLowerCase());
    } else {
      setJustPasted(false);
    }
  }, []);

  const goToUpgrade = useCallback(() => {
    onClose();
    history.push("/organization-settings/billing/plans");
  }, [onClose, history]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isValidFormat || duplicate || quotaReached || sending) return;
      onSubmit(normalized);
    },
    [isValidFormat, duplicate, quotaReached, sending, onSubmit, normalized]
  );

  if (!open) {
    return null;
  }

  let fieldError = null;
  if (!isEmpty) {
    if (isWildcard) {
      fieldError = (
        <>
          <span className={styles.errorLine}>
            Wildcard domains are not supported.
          </span>
          <span className={styles.errorLine}>
            Add each subdomain as its own website — e.g.{" "}
            <strong>shop.mydomain.com</strong>.
          </span>
        </>
      );
    } else if (!isValidFormat) {
      fieldError = (
        <>
          <span className={styles.errorLine}>That is not a valid domain.</span>
          <span className={styles.errorLine}>
            Enter a domain such as <strong>mydomain.com</strong> or{" "}
            <strong>shop.mydomain.com</strong> — letters, numbers, hyphens and
            dots only.
          </span>
        </>
      );
    } else if (duplicate) {
      fieldError = (
        <>
          <span className={styles.errorLine}>
            {normalized} is already on the list.
          </span>
          <span className={styles.errorLine}>
            Added{" "}
            {duplicate.created
              ? formatDateShort(duplicate.created * 1000)
              : "—"}
            {duplicate.creator?.name || duplicate.creator?.email
              ? ` by ${duplicate.creator.name || duplicate.creator.email}`
              : ""}
            . Subdomains are separate entries.
          </span>
        </>
      );
    }
  }

  const hint =
    !fieldError && justPasted ? (
      <>
        <Icon name="Check" size={14} className={styles.hintTick} />
        Saved as <strong>{normalized}</strong> — the protocol, www and path were
        removed.
      </>
    ) : !fieldError ? (
      <>
        Paste a full URL if it is easier — the protocol, www and any path are
        removed for you. Subdomains are separate entries: add{" "}
        <strong>shop.mydomain.com</strong> as its own website.
      </>
    ) : undefined;

  const canSubmit =
    !isEmpty && isValidFormat && !duplicate && !quotaReached && !sending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={<Icon name="GlobeSlated01" size={24} />}
      title="Add website"
      footer={
        <div className={styles.footerButtons}>
          {quotaReached ? (
            <>
              <Button
                hierarchy="secondaryGray"
                onClick={onClose}
                className={styles.footerBtn}
              >
                Cancel
              </Button>
              <Button
                hierarchy="primary"
                onClick={goToUpgrade}
                className={styles.footerBtn}
              >
                Upgrade plan
              </Button>
            </>
          ) : (
            <>
              <Button
                hierarchy="secondaryGray"
                onClick={onClose}
                disabled={sending}
                className={styles.footerBtn}
              >
                Cancel
              </Button>
              <Button
                hierarchy="primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
                loading={sending}
                className={styles.footerBtn}
              >
                {sending ? "Adding…" : "Add website"}
              </Button>
            </>
          )}
        </div>
      }
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <p className={styles.lede}>
          Enter the domain where your widgets will be embedded.
        </p>

        <Input
          label="Domain"
          placeholder="mydomain.com"
          value={rawValue}
          onChange={handleChange}
          error={fieldError}
          hint={hint}
          disabled={sending || quotaReached}
          autoFocus
          autoComplete="off"
        />

        {quotaReached ? (
          <div className={styles.quotaPanel}>
            <p className={styles.quotaPanelTitle}>
              You have used all {domainLimit} of your domains.
            </p>
            <p className={styles.quotaPanelText}>
              Remove a website you no longer embed on, or upgrade for more.
            </p>
          </div>
        ) : (
          domainLimit != null &&
          domainLimit >= 0 && (
            <p className={styles.quotaLine}>
              {domainsUsed} of {domainLimit} domains used.
            </p>
          )
        )}

        {submitError && <p className={styles.submitError}>{submitError}</p>}
      </form>
    </Modal>
  );
};

export default AddWebsiteDialog;
